import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://getemoji.ai';
const DEFAULT_TITLE = 'Get Emoji - Free Emoji Browser & Copy Tool 🙂📄👍😹🐱🐶 | 3400+ Emojis';
const DEFAULT_DESCRIPTION =
  'Free emoji browser with 3400+ emojis. Search, browse, and copy emojis with one click. Supports skin tones and multiple languages.';

// Route-based language availability (exact-vs-descendant matching).
// Only advertise hreflang for languages with actual content (TEAM pitfall #2).
// All routes — INDEX routes (key-based i18n) and their LEAF descendants
// (whole-HTML-per-language pages, now with en/es/pt/ru folders) — have all 4
// languages. `STATIC_LEAF_LANGUAGES` is kept as a DISTINCT constant (equal in
// value to `ALL_LANGUAGES` today, not in role): it is the single flip point
// for any FUTURE language, which will again land on the index routes before
// the leaf pages exist. When that happens, remove the new language here until
// its leaf pages are wired.
const ALL_LANGUAGES = ['en', 'es', 'ru', 'pt'];
const STATIC_LEAF_LANGUAGES = ['en', 'es', 'pt', 'ru'];
const STATIC_SECTIONS = ['/support', '/releases'];

// Canonical path normalization: strip trailing slashes and lowercase. React
// Router matches routes case-insensitively (so /Support/windows/ renders the
// leaf), so mixed-case and trailing-slash URLs are duplicates of the canonical
// lowercase, slash-less path. Used by BOTH availability matching and the
// emitted canonical/hreflang/x-default URLs so the two can never disagree.
// No-op for every currently-defined route (all lowercase, no trailing slash).
const normalizePath = (path: string): string => (path.replace(/\/+$/, '') || '/').toLowerCase();

export const getAvailableLanguages = (path: string): string[] => {
  const normalized = normalizePath(path);
  const isStaticLeaf = STATIC_SECTIONS.some((section) => normalized.startsWith(`${section}/`));
  return isStaticLeaf ? STATIC_LEAF_LANGUAGES : ALL_LANGUAGES;
};

interface SEOTagsProps {
  title?: string;
  description?: string;
  /**
   * When true, the canonical <link> is removed while this page is mounted
   * (restored on unmount) and hreflang alternates are not emitted. Used by the
   * soft-404 page: a noindexed route must not advertise a canonical or
   * language alternates. Mirrors the robots update-in-place lifecycle.
   */
  suppressCanonical?: boolean;
}

export const SEOTags = ({ title, description, suppressCanonical }: SEOTagsProps = {}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const location = useLocation();

  const pageTitle = title || DEFAULT_TITLE;
  const pageDescription = description || DEFAULT_DESCRIPTION;
  const currentPath = location.pathname;

  useEffect(() => {
    // Update title
    document.title = pageTitle;
    const titleElement = document.querySelector('title');
    if (titleElement) {
      titleElement.textContent = pageTitle;
    }

    const metaTitle = document.querySelector('meta[name="title"]') as HTMLMetaElement | null;
    if (metaTitle) {
      metaTitle.content = pageTitle;
    }

    const ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    if (ogTitle) {
      ogTitle.content = pageTitle;
    }

    const twitterTitle = document.querySelector(
      'meta[property="twitter:title"]'
    ) as HTMLMetaElement | null;
    if (twitterTitle) {
      twitterTitle.content = pageTitle;
    }

    const appleAppTitle = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]'
    ) as HTMLMetaElement | null;
    if (appleAppTitle) {
      appleAppTitle.content = pageTitle;
    }

    // Update description
    const metaDescription = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement | null;
    if (metaDescription) {
      metaDescription.content = pageDescription;
    }

    const ogDescription = document.querySelector(
      'meta[property="og:description"]'
    ) as HTMLMetaElement | null;
    if (ogDescription) {
      ogDescription.content = pageDescription;
    }

    const twitterDescription = document.querySelector(
      'meta[property="twitter:description"]'
    ) as HTMLMetaElement | null;
    if (twitterDescription) {
      twitterDescription.content = pageDescription;
    }

    // Language availability for THIS route (exact-vs-descendant map above) —
    // computed BEFORE the html[lang] write, which needs it.
    const available = getAvailableLanguages(currentPath);
    // When the active language has no content on this route (e.g. ?lang=ru on
    // /support/windows renders the English fallback), the URL is a duplicate
    // of the bare EN page — canonicalize to the bare path instead of
    // self-canonicalizing the duplicate.
    const langAvailable = available.includes(currentLang);
    // Effective CONTENT language: what the user actually reads on this route.
    // Unavailable language => the English fallback is rendered, so html[lang]
    // and og:locale must declare EN, not the requested language.
    const effectiveLang = langAvailable ? currentLang : 'en';

    // Update HTML lang attribute (effective content language)
    document.documentElement.lang = effectiveLang;

    // Canonical/hreflang/x-default all use the NORMALIZED path (case +
    // trailing-slash), so mixed-case duplicates self-canonicalize to one URL.
    const canonicalPath = normalizePath(currentPath);

    // Build URL with path and language
    const buildUrl = (path: string, lang?: string) => {
      const langParam = lang ? `?lang=${lang}` : '';
      return `${SITE_URL}${path}${langParam}`;
    };

    // Canonical: bare path for EN and for unavailable languages; ?lang= for
    // available non-EN languages so Google indexes them separately.
    const canonicalUrl =
      currentLang === 'en' || !langAvailable
        ? `${SITE_URL}${canonicalPath}`
        : `${SITE_URL}${canonicalPath}?lang=${currentLang}`;

    const head = document.head;

    // Remove existing hreflang links (always — recreated below unless suppressed).
    const existingHreflangLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflangLinks.forEach((link) => link.remove());

    // Suppression (soft-404): a noindexed route must not advertise a canonical
    // or language alternates. Remove the canonical element while mounted and
    // restore it in the effect cleanup (mirrors NotFoundPage's robots
    // update-in-place); skip hreflang recreation and the og/twitter URL writes.
    // Everything else (title, description, html[lang], og:locale, og/twitter
    // images) runs exactly as on a normal page.
    let removedCanonical: Element | null = null;
    if (suppressCanonical) {
      removedCanonical = document.querySelector('link[rel="canonical"]');
      removedCanonical?.remove();
    } else {
      const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonicalLink) {
        canonicalLink.href = canonicalUrl;
      }

      // Create new hreflang links
      const languages = [
        { code: 'en', label: 'English' },
        { code: 'es', label: 'Español' },
        { code: 'ru', label: 'Русский' },
        { code: 'pt', label: 'Português' },
      ];

      // Add hreflang only for languages with actual content on this route
      languages
        .filter(({ code }) => available.includes(code))
        .forEach(({ code }) => {
          const link = document.createElement('link');
          link.rel = 'alternate';
          link.hreflang = code;
          link.href = code === 'en' ? `${SITE_URL}${canonicalPath}` : buildUrl(canonicalPath, code);
          head.appendChild(link);
        });

      // Add x-default hreflang (English as default)
      const defaultLink = document.createElement('link');
      defaultLink.rel = 'alternate';
      defaultLink.hreflang = 'x-default';
      defaultLink.href = `${SITE_URL}${canonicalPath}`;
      head.appendChild(defaultLink);
    }

    // Update Open Graph locale tags (effective content language — matches
    // html[lang]; an unavailable language renders the EN fallback).
    const ogLocale = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement;
    if (ogLocale) {
      const localeMap: { [key: string]: string } = {
        en: 'en_US',
        es: 'es_ES',
        ru: 'ru_RU',
        pt: 'pt_BR',
      };
      ogLocale.content = localeMap[effectiveLang] || 'en_US';
    }

    // Update OG/Twitter URLs — same rule as the canonical (bare path for EN
    // and for languages without content on this route). Skipped when suppressed
    // (they follow the canonical; the residual previous-page URL is inert on a
    // noindexed page).
    if (!suppressCanonical) {
      const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
      if (ogUrl) {
        ogUrl.content = canonicalUrl;
      }

      const twitterUrl = document.querySelector('meta[property="twitter:url"]') as HTMLMetaElement;
      if (twitterUrl) {
        twitterUrl.content = canonicalUrl;
      }
    }

    // Update OG image (ensuring it's always set correctly)
    const ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
    if (ogImage) {
      ogImage.content = `${SITE_URL}/preview-image-01.png`;
    }

    const twitterImage = document.querySelector(
      'meta[property="twitter:image"]'
    ) as HTMLMetaElement;
    if (twitterImage) {
      twitterImage.content = `${SITE_URL}/preview-image-01.png`;
    }

    // Restore the suppressed canonical on cleanup so the next page's SEOTags
    // effect finds it to mutate (React runs this cleanup before the next
    // component's effects in the commit — on a lazy() route, after the Suspense
    // chunk resolves). Re-runs while mounted restore-then-remove (idempotent).
    if (removedCanonical) {
      const restored = removedCanonical;
      return () => {
        head.appendChild(restored);
      };
    }
  }, [currentLang, currentPath, pageTitle, pageDescription, suppressCanonical]);

  return null; // This component doesn't render anything
};
